/**
 * Client-side image compression utility.
 * Resizes large images (typically multi-MB phone photos) down to KB-range
 * via HTML5 Canvas + JPEG re-encoding. Used by KYC + payment screenshot uploads.
 *
 * Usage:
 *   const dataUrl = await compressImageFile(file);
 *   // dataUrl is a base64 JPEG, typically 100–400 KB even from 5MB originals
 */

const DEFAULT_OPTIONS = {
  maxWidth: 1280,      // Plenty for Aadhar / payment screenshots
  maxHeight: 1280,
  initialQuality: 0.78,
  targetMaxBytes: 480 * 1024, // 480 KB target
  minQuality: 0.4,
  mimeType: "image/jpeg",
};

/**
 * Compress an image File/Blob and return a base64 data URL.
 * Auto-retries with lower quality if the encoded size is still too large.
 */
export function compressImageFile(file, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided"));
      return;
    }
    if (!file.type?.startsWith("image/")) {
      // Not an image — just read as-is (e.g., PDFs unsupported here)
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error("File read failed"));
      reader.readAsDataURL(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Compute resized dimensions while keeping aspect ratio
        let { width, height } = img;
        if (width > opts.maxWidth || height > opts.maxHeight) {
          const scale = Math.min(opts.maxWidth / width, opts.maxHeight / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        // White background for JPEG transparency safety
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Encode with decreasing quality until size target met
        let quality = opts.initialQuality;
        let dataUrl = canvas.toDataURL(opts.mimeType, quality);
        let attempts = 0;
        while (dataUrl.length * 0.75 > opts.targetMaxBytes && quality > opts.minQuality && attempts < 6) {
          quality = Math.max(opts.minQuality, quality - 0.1);
          dataUrl = canvas.toDataURL(opts.mimeType, quality);
          attempts += 1;
        }
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error("Image decode failed"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

/**
 * Approximate size of a base64 data URL in bytes.
 */
export function approxBase64Bytes(dataUrl) {
  if (!dataUrl) return 0;
  const head = dataUrl.indexOf(",");
  const b64 = head >= 0 ? dataUrl.slice(head + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4);
}
